package net.markdrew.biblebowl.web.screens

import kotlinx.coroutines.launch
import net.markdrew.biblebowl.api.ChangePasswordRequest
import net.markdrew.biblebowl.api.ContactInfoDto
import net.markdrew.biblebowl.api.ContactPreference
import net.markdrew.biblebowl.api.ParticipationDto
import net.markdrew.biblebowl.api.PersonRelation
import net.markdrew.biblebowl.api.PersonWithParticipationsDto
import net.markdrew.biblebowl.api.UpdateProfileRequest
import net.markdrew.biblebowl.api.UserDto
import net.markdrew.biblebowl.api.division
import net.markdrew.biblebowl.api.divisionForBirthdate
import net.markdrew.biblebowl.api.isValidBirthdate
import net.markdrew.biblebowl.web.Routes
import net.markdrew.biblebowl.web.Session
import net.markdrew.biblebowl.web.Shell
import net.markdrew.biblebowl.web.child
import net.markdrew.biblebowl.web.clear
import net.markdrew.biblebowl.web.onClick
import org.w3c.dom.Element
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLOptionElement
import org.w3c.dom.HTMLSelectElement

/**
 * Account screen (docs/gui-redesign.md §5H): editable profile (display name + the birthdate/adult
 * fields that drive division eligibility), claiming a roster entry by coach-shared code, roles
 * held, sign out. Role-gated destinations live in the navbar user menu (NavMenu), not here.
 */
object AccountScreen {

    fun render(container: HTMLElement) {
        val user = Session.user
        if (user == null) {
            // Not signed in: the account route is just the sign-in form.
            AuthScreen.render(container, gate = true)
            return
        }

        container.child("h1", "page-title", user.displayName)
        container.child("p", "fs-5 mb-1", user.email)
        val division = user.division(Session.season)
        when {
            division != null -> container.child("p", "text-muted", "Division: ${division.displayName}")
            else -> container.child(
                "p", "text-muted",
                "No division yet — add your birthdate below (or mark yourself an adult).",
            )
        }

        profileCard(container, user)
        passwordCard(container)
        if (Session.registrationVisible) {
            val reloadPeople = peopleCard(container)
            claimCard(container, onClaimed = reloadPeople)
        }

        container.child("div", "card section-card mb-3") {
            child("div", "card-body") {
                child("h5", "card-title", "Roles")
                if (user.roles.isEmpty()) {
                    child("p", "text-muted mb-0", "Contestant (default)")
                } else {
                    user.roles.forEach { grant ->
                        child("p", "mb-1", grant.role.name.lowercase().replaceFirstChar { it.uppercase() })
                    }
                }
            }
        }

        container.child("div", "d-grid gap-2") {
            child("button", "btn btn-outline-primary", "Sign out") {
                setAttribute("type", "button")
                onClick {
                    Session.signOut()
                    Shell.navigate(Routes.STUDY) // → redirects to the site's study hub page
                }
            }
        }
    }

    /** Display name plus the adult/birthdate eligibility fields, saved via `PUT /auth/me`. */
    private fun profileCard(container: Element, user: UserDto) {
        container.child("div", "card section-card mb-3") {
            child("div", "card-body") {
                child("h5", "card-title", "Profile")
                val form = child("form")

                lateinit var name: HTMLInputElement
                form.child("div", "mb-3") {
                    child("label", "form-label", "Display name")
                    name = (child("input", "form-control") as HTMLInputElement).apply {
                        value = user.displayName
                        setAttribute("autocomplete", "name")
                    }
                }

                lateinit var adult: HTMLInputElement
                form.child("div", "form-check mb-3") {
                    adult = (child("input", "form-check-input") as HTMLInputElement).apply {
                        type = "checkbox"
                        id = "account-adult"
                        checked = user.adult
                    }
                    child("label", "form-check-label", "I'm an adult (18+ or finished high school)") {
                        setAttribute("for", "account-adult")
                    }
                }

                lateinit var birthdate: HTMLInputElement
                val birthdateRow = form.child("div", "mb-3") {
                    child("label", "form-label", "Birthdate")
                    birthdate = (child("input", "form-control") as HTMLInputElement).apply {
                        type = "date"
                        value = user.birthdate ?: ""
                        setAttribute("autocomplete", "bday")
                    }
                    child("div", "form-text", "Used to place contestants in the right division each season.")
                }
                val divisionHint = form.child("p", "form-text mt-n2 mb-3", "")

                // Contact info (adults): optional event-communication details for registrars.
                fun contactInput(parent: Element, label: String, initial: String, autocomplete: String, cls: String = "") =
                    (parent.child("div", cls.ifEmpty { "flex-grow-1" }) {
                        child("label", "form-label", label)
                    }.let { wrap ->
                        wrap.child("input", "form-control") as HTMLInputElement
                    }).apply {
                        value = initial
                        setAttribute("autocomplete", autocomplete)
                    }
                val storedContact = user.contact ?: ContactInfoDto()
                lateinit var address: HTMLInputElement
                lateinit var city: HTMLInputElement
                lateinit var state: HTMLInputElement
                lateinit var zip: HTMLInputElement
                lateinit var phone: HTMLInputElement
                lateinit var preference: HTMLSelectElement
                val contactRows = form.child("div", "mb-3") {
                    child("h6", "mt-2", "Contact info")
                    child("p", "form-text mt-0",
                        "Optional — how event organizers can reach you (only registrars see it).")
                    address = contactInput(this, "Street address", storedContact.address, "street-address", "mb-2")
                    child("div", "d-flex flex-wrap gap-2 mb-2") {
                        city = contactInput(this, "City", storedContact.city, "address-level2")
                        state = contactInput(this, "State", storedContact.state, "address-level1", "w-25")
                        zip = contactInput(this, "Zip", storedContact.zip, "postal-code", "w-25")
                    }
                    child("div", "d-flex flex-wrap gap-2") {
                        phone = contactInput(this, "Phone", storedContact.phone, "tel")
                        child("div", "w-50") {
                            child("label", "form-label", "Preferred contact method")
                            preference = child("select", "form-select") as HTMLSelectElement
                            (preference.child("option", text = "No preference") as HTMLOptionElement).value = ""
                            ContactPreference.entries.forEach { pref ->
                                val option = preference.child("option", text = pref.displayName) as HTMLOptionElement
                                option.value = pref.name
                                if (pref == storedContact.preference) option.selected = true
                            }
                        }
                    }
                }

                val save = form.child("button", "btn btn-primary", "Save profile") {
                    setAttribute("type", "submit")
                } as HTMLButtonElement
                val messageSlot = form.child("div")

                fun refresh() {
                    val isAdult = adult.checked
                    birthdateRow.classList.toggle("d-none", isAdult)
                    contactRows.classList.toggle("d-none", !isAdult)
                    divisionHint.textContent = when {
                        isAdult -> "Division: Adult"
                        else -> birthdate.value.takeIf { it.isNotBlank() }
                            ?.let { Session.season.divisionForBirthdate(it) }
                            ?.let { "Division: ${it.displayName}" }
                            ?: ""
                    }
                    save.disabled = name.value.isBlank() ||
                        !(isAdult || birthdate.value.let { it.isNotBlank() && isValidBirthdate(it) })
                }
                refresh()
                listOf(name, adult, birthdate).forEach { it.addEventListener("input", { refresh() }) }

                form.addEventListener("submit", { event ->
                    event.preventDefault()
                    save.disabled = true
                    messageSlot.clear()
                    Shell.scope.launch {
                        try {
                            Session.api.updateProfile(
                                UpdateProfileRequest(
                                    displayName = name.value.trim(),
                                    birthdate = birthdate.value.takeIf { it.isNotBlank() }
                                        ?.takeUnless { adult.checked },
                                    adult = adult.checked,
                                    // Always sent (empty clears); the server keeps stored contact
                                    // only when the field is omitted entirely (older clients).
                                    contact = ContactInfoDto(
                                        address = address.value.trim(),
                                        city = city.value.trim(),
                                        state = state.value.trim(),
                                        zip = zip.value.trim(),
                                        phone = phone.value.trim(),
                                        preference = preference.value.takeIf { it.isNotEmpty() }
                                            ?.let { ContactPreference.valueOf(it) },
                                    ),
                                )
                            )
                            Session.profileSaved() // re-renders the screen with the fresh user
                        } catch (e: Throwable) {
                            messageSlot.child("p", "text-danger mt-3 mb-0", "Save failed: ${e.message}")
                            refresh()
                        }
                    }
                })
            }
        }
    }

    /** Change password: proves the current password, then swaps in the new one (`POST /auth/change-password`). */
    private fun passwordCard(container: Element) {
        container.child("div", "card section-card mb-3") {
            child("div", "card-body") {
                child("h5", "card-title", "Change password")
                val form = child("form")

                fun passwordField(label: String, autocomplete: String, help: String? = null): HTMLInputElement {
                    lateinit var input: HTMLInputElement
                    form.child("div", "mb-3") {
                        child("label", "form-label", label)
                        input = (child("input", "form-control") as HTMLInputElement).apply {
                            type = "password"
                            setAttribute("autocomplete", autocomplete)
                        }
                        help?.let { child("div", "form-text", it) }
                    }
                    return input
                }
                val current = passwordField("Current password", "current-password")
                val newPassword = passwordField("New password", "new-password", help = "At least 8 characters.")

                val submit = form.child("button", "btn btn-primary", "Change password") {
                    setAttribute("type", "submit")
                } as HTMLButtonElement
                val messageSlot = form.child("div")

                fun refresh() {
                    submit.disabled = current.value.isEmpty() || newPassword.value.length < 8
                }
                refresh()
                listOf(current, newPassword).forEach { it.addEventListener("input", { refresh() }) }

                form.addEventListener("submit", { event ->
                    event.preventDefault()
                    submit.disabled = true
                    messageSlot.clear()
                    Shell.scope.launch {
                        try {
                            Session.api.changePassword(
                                ChangePasswordRequest(current.value, newPassword.value)
                            )
                            current.value = ""
                            newPassword.value = ""
                            messageSlot.child("p", "tbb-gold fw-semibold mt-3 mb-0", "Password changed.")
                        } catch (e: Throwable) {
                            messageSlot.child("p", "text-danger mt-3 mb-0", "Change failed: ${e.message}")
                        } finally {
                            refresh()
                        }
                    }
                })
            }
        }
    }

    /**
     * The people this account is or manages, with their per-season participations — the list the
     * claim card feeds. Returns a reload function so a fresh claim can refresh it in place.
     */
    private fun peopleCard(container: Element): () -> Unit {
        lateinit var body: HTMLElement
        container.child("div", "card section-card mb-3") {
            child("div", "card-body") {
                child("h5", "card-title", "My people")
                child(
                    "p", "text-muted",
                    "Contestants and other attendees you've claimed. You'll see their scores here once " +
                        "they're released.",
                )
                body = child("div") as HTMLElement
            }
        }

        fun reload() {
            body.clear()
            body.child("p", "text-muted mb-0", "Loading…")
            Shell.scope.launch {
                try {
                    val people = Session.api.myPeople().people
                    body.clear()
                    if (people.isEmpty()) {
                        body.child("p", "text-muted mb-0", "You haven't claimed anyone yet.")
                        return@launch
                    }
                    people.forEach { pwp -> renderPerson(body, pwp) }
                } catch (e: Throwable) {
                    body.clear()
                    body.child("p", "text-danger mb-0", "Couldn't load your people: ${e.message}")
                }
            }
        }
        reload()
        return ::reload
    }

    /** One claimed person: name + relation, division, and a line per season participation. */
    private fun renderPerson(container: Element, pwp: PersonWithParticipationsDto) {
        val person = pwp.person
        container.child("div", "border-top pt-2 mt-2") {
            child("div", "d-flex align-items-baseline gap-2") {
                child("span", "fw-semibold", person.name)
                val relation = if (person.relation == PersonRelation.SELF) "You" else "You manage"
                child("span", "badge text-bg-secondary", relation)
            }
            person.division(Session.season)?.let { division ->
                child("p", "text-muted small mb-1", "Division: ${division.displayName}")
            }
            if (pwp.participations.isEmpty()) {
                child("p", "text-muted small mb-0", "Not registered for any season yet.")
            } else {
                pwp.participations.forEach { p ->
                    child("p", "small mb-0", participationLine(p))
                }
            }
        }
    }

    /** "2027 · First Baptist — Contestant, Coach · Team Lions · Tester #12" for one participation. */
    private fun participationLine(p: ParticipationDto): String {
        val roles = buildList {
            if (p.isContestant) add("Contestant")
            if (p.isCoach) add("Coach")
            addAll(p.positions)
        }.joinToString(", ").ifEmpty { "Attendee" }
        return buildString {
            append("${p.seasonYear} · ${p.congregationName} — $roles")
            p.teamName?.let { append(" · Team $it") }
            p.testerId?.let { append(" · Tester #$it") }
        }
    }

    /**
     * Claim a person by the coach-shared code, linking it to this account — that link is what My
     * Scores' owner scoping keys off (dashes/case in the code are tolerated server-side). On success
     * [onClaimed] refreshes the My-people list so the freshly-claimed person appears immediately.
     */
    private fun claimCard(container: Element, onClaimed: () -> Unit) {
        container.child("div", "card section-card mb-3") {
            child("div", "card-body") {
                child("h5", "card-title", "Claim a contestant")
                child(
                    "p", "text-muted",
                    "Enter the claim code your coach shared (like ABCD-2345) to link a contestant to this " +
                        "account and see their scores once they're released. If the code is your own and " +
                        "your email matches, it becomes you; otherwise you'll manage them (e.g. a parent " +
                        "claiming a child).",
                )
                val form = child("form", "d-flex flex-wrap gap-2 align-items-start")
                val code = (form.child("input", "form-control") as HTMLInputElement).apply {
                    setAttribute("placeholder", "ABCD-2345")
                    setAttribute("autocomplete", "off")
                    style.maxWidth = "12rem"
                }
                val claim = form.child("button", "btn btn-primary", "Claim") {
                    setAttribute("type", "submit")
                } as HTMLButtonElement
                val messageSlot = child("div")
                form.addEventListener("submit", { event ->
                    event.preventDefault()
                    if (code.value.isBlank()) return@addEventListener
                    claim.disabled = true
                    messageSlot.clear()
                    Shell.scope.launch {
                        try {
                            val result = Session.api.claimPerson(code.value)
                            code.value = ""
                            onClaimed()
                            val who = if (result.relation == PersonRelation.SELF) "you" else result.person.name
                            messageSlot.child("p", "tbb-gold fw-semibold mt-2 mb-0") {
                                append("Claimed ${result.person.name} (as $who) — see ")
                                child("a", text = "My scores") { setAttribute("href", "#${Routes.MY_SCORES}") }
                                append(" once scores are released.")
                            }
                        } catch (e: Throwable) {
                            messageSlot.child("p", "text-danger mt-2 mb-0", "Claim failed: ${e.message}")
                        } finally {
                            claim.disabled = false
                        }
                    }
                })
            }
        }
    }

}
